import React, { useState } from 'react';
import { toast } from 'sonner';
import { ArrowsClockwise, GitMerge } from '@phosphor-icons/react';
import api from '../services/api';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import MergePreview from './merge/MergePreview';
import MergeSuccess from './merge/MergeSuccess';
import RecordCard from './merge/RecordCard';
import { computeMergedPreview, fieldsForType } from './merge/mergeFields';

/**
 * MergeDialog — side-by-side duplicate comparison + merge.
 *
 * Props:
 *   isOpen, onClose
 *   mergeType: 'patient' | 'employee'
 *   group: { match_value, match_type, count, patients|employees: [...] }
 *   onMergeComplete: callback after successful merge
 */
const MergeDialog = ({ isOpen, onClose, mergeType, group, onMergeComplete }) => {
  const [primaryId, setPrimaryId] = useState(null);
  const [merging, setMerging] = useState(false);
  const [result, setResult] = useState(null);

  const records = mergeType === 'patient' ? group?.patients : group?.employees;
  if (!records || records.length < 2) return null;

  const displayFields = fieldsForType(mergeType);
  const merged = computeMergedPreview(records, primaryId, displayFields);

  const handleMerge = async () => {
    if (!primaryId) {
      toast.error('Select a primary record first');
      return;
    }
    const dupeIds = records.filter((r) => r.id !== primaryId).map((r) => r.id);
    setMerging(true);
    try {
      const endpoint = mergeType === 'patient' ? '/patients/merge' : '/employees/merge';
      const res = await api.post(endpoint, {
        primary_id: primaryId,
        duplicate_ids: dupeIds,
      });
      setResult(res.data);
      toast.success(res.data.message);
      onMergeComplete?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Merge failed');
    } finally {
      setMerging(false);
    }
  };

  const handleClose = () => {
    setPrimaryId(null);
    setResult(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <GitMerge size={22} className="text-indigo-600" />
            Merge {records.length} Duplicate {mergeType === 'patient' ? 'Patients' : 'Employees'}
          </DialogTitle>
          <DialogDescription>
            Select the primary record (keeper). Empty fields will be filled from duplicates.
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <MergeSuccess result={result} />
        ) : (
          <div className="space-y-4">
            <div
              className="grid gap-3"
              style={{ gridTemplateColumns: `repeat(${Math.min(records.length, 3)}, 1fr)` }}
            >
              {records.map((rec) => (
                <RecordCard
                  key={rec.id}
                  record={rec}
                  isPrimary={primaryId === rec.id}
                  displayFields={displayFields}
                  onSelect={setPrimaryId}
                />
              ))}
            </div>

            {primaryId && merged && <MergePreview merged={merged} displayFields={displayFields} />}

            {!primaryId && (
              <p className="text-center text-sm text-slate-500 py-2">
                Click a record above to select it as the primary (keeper)
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          {result ? (
            <Button onClick={handleClose} className="bg-indigo-900 hover:bg-indigo-800" data-testid="merge-done-btn">
              Done
            </Button>
          ) : (
            <div className="flex justify-between w-full">
              <Button variant="outline" onClick={handleClose}>Cancel</Button>
              <Button
                onClick={handleMerge}
                disabled={!primaryId || merging}
                className="bg-indigo-900 hover:bg-indigo-800"
                data-testid="confirm-merge-btn"
              >
                {merging ? (
                  <ArrowsClockwise size={16} className="mr-2 animate-spin" />
                ) : (
                  <GitMerge size={16} className="mr-2" />
                )}
                Merge into Primary
              </Button>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MergeDialog;
